import React from 'react';
import { IProps, IFormValue, CI_CV_TYPE, IState } from './types';
import { Formik, Form, Field, ErrorMessage, FormikProps, FieldArray } from 'formik';
import { RouterSettings } from 'constants/models/routerSettings';
import service from 'bff/services/memberCorrectionService';
import { Code, Inscat, SysOption } from 'bff/models/info';
import _ from 'lodash';
import { InsType } from 'constants/enums/insType.enum';
import { ClaimPolicyNoContent, EndorseOptContent, EndorseOptContentTarget, EndorseOpts } from 'bff/models/member';
import commonService from 'utils/commonService';
import { EndorseTarget } from 'constants/enums/endorseTarget.enum';
import { EndorseType } from 'constants/enums/endorseType';
import { EndorseOption } from 'constants/models/endorseOption';
import CorrectionEndorseItem from '../CorrectionEndorseItem';
import * as Yup from 'yup';
import { EndorseAttachmentType } from 'constants/enums/endorseAttachmentType';
import { EndorseColType } from 'constants/enums/endorseColType';
import { mobileRegex, emailRegex } from 'utils/commonValidations';
import { NumAndEnglishDashRegex } from 'utils/commonValidations';
import FormikFocusError from 'components/FormikFocusError';
import storageService from 'utils/storageService';
import { storageKeys } from 'constants/enums/storageKeys.enum';
import { VehicleItemType } from 'constants/enums/vehicleItemType';

let formInitialValues: IFormValue = {
  INS_TYPE: InsType.Car,
  POLICY_NUMBER: '',
  APPLY_NO: '',
  COMPULSORY_NO: '',
  COMPULSORY_ONLY: false,
  CAR_LICENSE: '',
  ENDORSE_OPTS: new Array(0),
  CI_CV_TYPES: new Array(0),
  ORIGINAL_ENDORSE: {
    OPTION: new Array(0),
    FILES: new Array(0)
  }
};

const formValidationSchema = Yup.object().shape({
  INS_TYPE: Yup.string().required('此為必填欄位'),
  POLICY_NUMBER: Yup.string().when('COMPULSORY_ONLY', (compulsoryOnly: boolean, schema: any) => {
    return !compulsoryOnly ? schema.required('此為必填欄位') : schema;
  }).nullable(),
  COMPULSORY_NO: Yup.string().when('COMPULSORY_ONLY', (compulsoryOnly: boolean, schema: any) => {
    return compulsoryOnly ? schema.required('此為必填欄位') : schema;
  }).nullable(),
  ENDORSE_OPTS: Yup.array().of(
    Yup.object().shape({
      FILE_FIELD_NAME: Yup.string().test('required', '此為必填欄位', function (val) {
        const { CHECKED, ATTACHMENT } = this.parent;
        if (CHECKED && ATTACHMENT === EndorseAttachmentType.Required) {
          return val;
        }

        return true;
      }),
      POST_CODE: Yup.string().test('required', '此為必填欄位', function (val) {
        const { path, parent } = this;
        const { CHECKED, DATA_TYPE } = parent;
        let errMsg = '';

        if (CHECKED && DATA_TYPE === EndorseColType.ADDRESS && !val) {
          errMsg = '此為必填欄位';
        }

        if (!errMsg && CHECKED && DATA_TYPE === EndorseColType.ADDRESS && val && val.length > 6) {
          errMsg = '郵遞區號長度不可超過 6 字元';
        }

        return errMsg ? this.createError({ path, message: errMsg }) : true;
      }),
      CITY_ID: Yup.string().test('required', '此為必填欄位', function (val) {
        const { CHECKED, DATA_TYPE } = this.parent;
        if (CHECKED && DATA_TYPE === EndorseColType.ADDRESS) {
          return val;
        }

        return true;
      }),
      AREA_ID: Yup.string().test('required', '此為必填欄位', function (val) {
        const { CHECKED, DATA_TYPE } = this.parent;
        if (CHECKED && DATA_TYPE === EndorseColType.ADDRESS) {
          return val;
        }

        return true;
      }),
      ADDRESS: Yup.string().test('has error', '檢核錯誤', function (val) {
        const { path, parent } = this;
        const { CHECKED, DATA_TYPE } = parent;
        let errMsg = '';

        if (CHECKED && DATA_TYPE === EndorseColType.ADDRESS && !val) {
          errMsg = '此為必填欄位';
        }

        if (!errMsg && CHECKED && DATA_TYPE === EndorseColType.ADDRESS && val && val.length > 200) {
          errMsg = '詳細地址長度不可超過 200 字元';
        }

        return errMsg ? this.createError({ path, message: errMsg }) : true;
      }),
      MOBILE: Yup.string().test('has error', '檢核錯誤', function (val) {
        const { path, parent } = this;
        const { CHECKED, DATA_TYPE } = parent;
        let errMsg = '';

        if (CHECKED && DATA_TYPE === EndorseColType.MOBILE && !val) {
          errMsg = '此為必填欄位';
        }

        if (!errMsg && CHECKED && DATA_TYPE === EndorseColType.MOBILE && val && val.length !== 10) {
          errMsg = '手機號碼長度為 10 字元';
        }

        if (!errMsg && CHECKED && DATA_TYPE === EndorseColType.MOBILE && val && !mobileRegex.test(val)) {
          errMsg = '手機格式不符';
        }

        return errMsg ? this.createError({ path, message: errMsg }) : true;
      }),
      EMAIL: Yup.string().email('電子信箱格式不符').test('has error', '檢核錯誤', function (val) {
        const { path, parent } = this;
        const { CHECKED, DATA_TYPE } = parent;
        let errMsg = '';

        if (CHECKED && DATA_TYPE === EndorseColType.EMAIL && !val) {
          errMsg = '此為必填欄位';
        }

        if (!errMsg && CHECKED && DATA_TYPE === EndorseColType.EMAIL && val && val.length > 50) {
          errMsg = '電子信箱長度不可超過 50 字元';
        }

        if (!errMsg && CHECKED && DATA_TYPE === EndorseColType.EMAIL && val && !emailRegex.test(val)) {
          errMsg = '電子信箱格式不符';
        }

        return errMsg ? this.createError({ path, message: errMsg }) : true;
      }),
      MARRIAGE: Yup.string().test('required', '此為必填欄位', function (val) {
        const { CHECKED, DATA_TYPE } = this.parent;
        if (CHECKED && DATA_TYPE === EndorseColType.RADIO) {
          return val;
        }

        return true;
      }),
      NAME: Yup.string().test('has error', '檢核錯誤', function (val) {
        const { path, parent } = this;
        const { CHECKED, DATA_TYPE, ENDORSE_TYPE } = parent;

        let errMsg = '';

        if (CHECKED && DATA_TYPE === EndorseColType.STRING && ENDORSE_TYPE === EndorseType.NAME && !val) {
          errMsg = `此為必填欄位`;
        }

        if (!errMsg && CHECKED && DATA_TYPE === EndorseColType.STRING && val && (/[\d]+/.test(val))) {
          errMsg = `姓名格式不符`;
        }

        if (!errMsg && CHECKED && DATA_TYPE === EndorseColType.STRING && val && val.length > 200) {
          errMsg = `姓名長度不可超過 200 字元`;
        }

        return errMsg ? this.createError({ path, message: errMsg }) : true;
      }),
      ENGINE_ID: Yup.string().test('has error', '檢核錯誤', function (val) {
        const { path, parent } = this;
        const { CHECKED, DATA_TYPE, ENDORSE_TYPE } = parent;

        let errMsg = '';

        if (CHECKED && DATA_TYPE === EndorseColType.STRING && ENDORSE_TYPE === EndorseType.ENGINE_ID && !val) {
          errMsg = `此為必填欄位`;
        }

        if (!errMsg && CHECKED && DATA_TYPE === EndorseColType.STRING && ENDORSE_TYPE === EndorseType.ENGINE_ID && val && !NumAndEnglishDashRegex.test(val)) {
          errMsg = `格式不符`;
        }

        if (!errMsg && CHECKED && DATA_TYPE === EndorseColType.STRING && ENDORSE_TYPE === EndorseType.ENGINE_ID && val && val.length > 20) {
          errMsg = `長度不可超過 20 字元`;
        }

        return errMsg ? this.createError({ path, message: errMsg }) : true;
      }),
      VEHICLE_ID: Yup.string().test('has error', '檢核錯誤', function (val) {
        const { path, parent } = this;
        const { CHECKED, DATA_TYPE, ENDORSE_TYPE } = parent;

        let errMsg = '';

        if (CHECKED && DATA_TYPE === EndorseColType.STRING && ENDORSE_TYPE === EndorseType.VEHICLE_ID && !val) {
          errMsg = `此為必填欄位`;
        }

        if (!errMsg && CHECKED && DATA_TYPE === EndorseColType.STRING && ENDORSE_TYPE === EndorseType.VEHICLE_ID && val && !NumAndEnglishDashRegex.test(val)) {
          errMsg = `格式不符`;
        }

        if (!errMsg && CHECKED && DATA_TYPE === EndorseColType.STRING && ENDORSE_TYPE === EndorseType.VEHICLE_ID && val && val.length > 20) {
          errMsg = `長度不可超過 20 字元`;
        }

        return errMsg ? this.createError({ path, message: errMsg }) : true;
      }),
      CAR_LICENSE_FRONT: Yup.string().test('has error', '檢核錯誤', function (val) {
        const { path, parent } = this;
        const { CHECKED, DATA_TYPE, ENDORSE_TYPE } = parent;

        let errMsg = '';

        if (CHECKED && DATA_TYPE === EndorseColType.STRING && ENDORSE_TYPE === EndorseType.CAR_LICENSE && !val) {
          errMsg = `此為必填欄位`;
        }

        if (!errMsg && CHECKED && DATA_TYPE === EndorseColType.STRING && ENDORSE_TYPE === EndorseType.CAR_LICENSE && val && val.length > 4) {
          errMsg = `長度不可超過 4 字元`;
        }

        return errMsg ? this.createError({ path, message: errMsg }) : true;
      }),
      CAR_LICENSE_BACK: Yup.string().test('has error', '檢核錯誤', function (val) {
        const { path, parent } = this;
        const { CHECKED, DATA_TYPE, ENDORSE_TYPE } = parent;

        let errMsg = '';

        if (CHECKED && DATA_TYPE === EndorseColType.STRING && ENDORSE_TYPE === EndorseType.CAR_LICENSE && !val) {
          errMsg = `此為必填欄位`;
        }

        if (!errMsg && CHECKED && DATA_TYPE === EndorseColType.STRING && ENDORSE_TYPE === EndorseType.CAR_LICENSE && val && val.length > 4) {
          errMsg = `長度不可超過 4 字元`;
        }

        return errMsg ? this.createError({ path, message: errMsg }) : true;
      }),
      ISSUE_DATE_YEAR: Yup.string().test('required', '此為必填欄位', function (val) {
        const { CHECKED, ENDORSE_TYPE } = this.parent;
        if (CHECKED && ENDORSE_TYPE === EndorseType.ISSUE_DATE) {
          return val;
        }

        return true;
      }),
      ISSUE_DATE_MONTH: Yup.string().test('required', '此為必填欄位', function (val) {
        const { CHECKED, ENDORSE_TYPE } = this.parent;
        if (CHECKED && ENDORSE_TYPE === EndorseType.ISSUE_DATE) {
          return val;
        }

        return true;
      }),
      ISSUE_DATE_DATE: Yup.string().test('required', '此為必填欄位', function (val) {
        const { CHECKED, ENDORSE_TYPE } = this.parent;
        if (CHECKED && ENDORSE_TYPE === EndorseType.ISSUE_DATE) {
          return val;
        }

        return true;
      })
    })
  )
});

const initState: IState = {
  emailDomains: new Array(0),
  postCodes: new Array(0),
  insCats: new Array(0),
  marriageSysOpts: new Array(0),
  vehicleCVTypeSysOpts: new Array(0),
  insType: '',
  applyNo: '',
  policyInfos: new Array(0),
  endorseCodes: new Array(0),
  endorse: {
    OPTION: new Array(0),
    FILES: new Array(0)
  },
  UICodes: new Array(0),
}

class CorrectionVehicle extends React.Component<IProps, IState> {
  private formikRef = React.createRef<FormikProps<IFormValue>>();
  state: IState = initState;

  componentDidMount = async () => {
    const tempVal = storageService.getItem(storageKeys.CorrectionFormVal);
    if (tempVal) {
      const formVal = JSON.parse(tempVal) as IFormValue;

      const fetchPolicyNosreq = {
        INS_TYPE_ID: formVal.INS_TYPE,
        APPLICANT_ID: storageService.getItem(storageKeys.MemberId)
      }

      const initSetting = await service.fetchVehicleInitSetting(fetchPolicyNosreq);
      if (!initSetting) return;
      const endorse = await service.fetchEndorse(formVal.INS_TYPE, formVal.APPLY_NO);
      if (!endorse) return;

      const policyInfo = _.find(initSetting.policyInfos, (policyInfo: ClaimPolicyNoContent) => policyInfo.APPLY_NO === formVal.APPLY_NO);

      let newPolicyInfos = [...initSetting.policyInfos];
      if (policyInfo) {
        newPolicyInfos = _.filter(initSetting.policyInfos,
          (original: ClaimPolicyNoContent) =>
            original.COMPULSORY === policyInfo.COMPULSORY && original.VOLUNTARY === policyInfo.VOLUNTARY
        )
      }

      formInitialValues = {
        ...formInitialValues,
        ...formVal
      }

      this.setState({
        insType: formVal.INS_TYPE,
        applyNo: formVal.APPLY_NO,
        insCats: [...initSetting.inscats],
        emailDomains: [...initSetting.emailDomains],
        postCodes: [...initSetting.postcodes],
        marriageSysOpts: [...initSetting.marriageSysOpts],
        endorseCodes: [...initSetting.endorseCodes],
        UICodes: [...initSetting.UICodes],
        vehicleCVTypeSysOpts: [...initSetting.vehicleCVTypeSysOpts],
        policyInfos: [...newPolicyInfos],
        endorse: endorse as EndorseOpts
      });

      return;
    }
    let insTypeIdVal: any;
    let policyNoVal: any;
    let applyNoVal: any;
    if (this.props.history.location.state) {
      // 透過路由取得險種類別
      const { insTypeId, policyNo, applyNo }: any = this.props.history.location.state;
      insTypeIdVal = insTypeId;
      policyNoVal = policyNo;
      applyNoVal = applyNo;
    }

    if (!insTypeIdVal || !policyNoVal) {
      this.props.history.replace(RouterSettings.MemberCenter.PolicyInquiry);
      return;
    }

    const fetchPolicyNosreq = {
      INS_TYPE_ID: insTypeIdVal,
      APPLICANT_ID: storageService.getItem(storageKeys.MemberId)
    }

    const initSetting = await service.fetchVehicleInitSetting(fetchPolicyNosreq);
    if (!initSetting) return;
    const endorse = await service.fetchEndorse(insTypeIdVal, applyNoVal);
    if (!endorse) return;

    this.setState(prevState => {
      return {
        ...prevState,
        postCodes: [...initSetting.postcodes]
      }
    });

    const policyInfo = _.find(initSetting.policyInfos, (policyInfo: ClaimPolicyNoContent) => policyInfo.APPLY_NO === applyNoVal);

    let newPolicyInfos = [...initSetting.policyInfos];
    if (policyInfo) {
      newPolicyInfos = _.filter(initSetting.policyInfos,
        (original: ClaimPolicyNoContent) =>
          original.COMPULSORY === policyInfo.COMPULSORY && original.VOLUNTARY === policyInfo.VOLUNTARY
      )
    }

    const cicyTypes = this.handleGetCICYTypes(initSetting.vehicleCVTypeSysOpts, policyInfo);
    const compulsoryOnly = this.handleCheckCompulsoryOnly(cicyTypes);

    formInitialValues = {
      ...formInitialValues,
      INS_TYPE: insTypeIdVal,
      ENDORSE_OPTS: this.handleGetEndorseOpts(endorse.OPTION),
      ORIGINAL_ENDORSE: endorse,
      CI_CV_TYPES: cicyTypes,
      COMPULSORY_ONLY: compulsoryOnly,
      COMPULSORY_NO: policyInfo ? policyInfo.COMPULSORY_NO : '',
      CAR_LICENSE: policyInfo ? policyInfo.CAR_LICENSE : '',
      POLICY_NUMBER: policyInfo ? policyInfo.POLICY_NO : '',
      APPLY_NO: policyInfo ? policyInfo.APPLY_NO : '',
    }

    this.setState(prevState => {
      return {
        ...prevState,
        insType: insTypeIdVal,
        applyNo: applyNoVal,
        insCats: [...initSetting.inscats],
        emailDomains: [...initSetting.emailDomains],
        marriageSysOpts: [...initSetting.marriageSysOpts],
        endorseCodes: [...initSetting.endorseCodes],
        UICodes: [...initSetting.UICodes],
        vehicleCVTypeSysOpts: [...initSetting.vehicleCVTypeSysOpts],
        policyInfos: [...newPolicyInfos],
        endorse: endorse as EndorseOpts
      }
    });
  }

  componentDidUpdate() {
    const emailInput = document.getElementById('email-input') as HTMLInputElement;
    if (emailInput) {
      emailInput.setAttribute('maxlength', '50');
      emailInput.setAttribute('autocomplete', 'off');
    }
  }

  componentWillUnmount() {
    if (this.props.history.location &&
      this.props.history.location.pathname &&
      this.props.history.location.pathname !== RouterSettings.MemberCenter.CorrectionPreview) {
      storageService.removeItem(storageKeys.CorrectionFormVal);
    }
  }


  /**
 * @description 取得 CI CV Type 選單內容
 */
  handleGetCICYTypes = (sysOpts: SysOption[], policyInfo?: ClaimPolicyNoContent) => {
    return _.map(sysOpts, (sysOpt: SysOption) => {
      return {
        CHECKED: policyInfo && sysOpt.PARAM_VALUE === VehicleItemType.CV_TYPE_CI
          ? policyInfo.COMPULSORY
          : policyInfo && sysOpt.PARAM_VALUE === VehicleItemType.CV_TYPE_VI
            ? policyInfo.VOLUNTARY
            : false,
        TYPE: sysOpt.PARAM_VALUE,
        TEXT: sysOpt.PARAM_TEXT
      }
    })
  }

  /**
   * @description 檢核是否只有勾選強制險項目
   */
  handleCheckCompulsoryOnly = (civiTypes: any) => {
    const ciType = _.find(civiTypes, (type) => type.TYPE === VehicleItemType.CV_TYPE_CI);

    // 判斷有無強制險項目
    if (!ciType)
      return false;

    // 判斷有強制險項目，但未勾選
    if (ciType && !ciType.CHECKED)
      return false;

    const viType = _.find(civiTypes, (type) => type.TYPE === VehicleItemType.CV_TYPE_VI);

    // 判斷有強制險項目，且勾選，且有任意險項目，且勾選
    if (ciType.CHECKED && viType && viType.CHECKED)
      return false;

    // 判斷有強制險項目，且勾選，且無任意險項目，且未勾選
    if (ciType.CHECKED && (!viType || !viType.CHECKED))
      return true;

    return false;
  }

  /**
   * @description 取得可變更項目
   */
  handleGetEndorseOpts = (opts: EndorseOptContent[]) => {
    return _.map(opts, (opt: EndorseOptContent) => {
      return {
        CHECKED: false,
        ENDORSE_TYPE: opt.ENDORSE_TYPE,
        DATA_TYPE: opt.DATA_TYPE,
        ORDER_NO: opt.ORDER_NO,
        TYPE_NAME: opt.TYPE_NAME,
        ATTACHMENT: opt.ATTACHMENT,
        ATTACHMENT_NAME: opt.ATTACHMENT_NAME,
        ATTACHMENT_NOTE: opt.ATTACHMENT_NOTE,
        FILE_FIELD_NAME: '',
        FILE_BASE64: '',
        ...this.handleGetOriginalInfo(opt)
      };
    });
  }

  /**
  * @description 取得項目原始資料值
  */
  handleGetOriginalVal = (targets: EndorseOptContentTarget[], targetType: string) => {
    return _.find(targets,
      (target: EndorseOptContentTarget) => target.ENDORSE_TARGET === targetType)?.ORG_VALUE || '';
  };

  /**
 * @description 取得可變更項目預設值
 */
  handleGetOriginalInfo = (opt: EndorseOptContent) => {
    switch (opt.ENDORSE_TYPE) {
      case EndorseType.CONTACT_ADDRESS:
        return {
          POST_CODE: this.handleGetOriginalVal(opt.TARGET, EndorseTarget.APPLICANT_POSTCODE),
          CITY_ID: this.handleGetOriginalVal(opt.TARGET, EndorseTarget.APPLICANT_CITY_ID),
          AREA_ID: this.handleGetOriginalVal(opt.TARGET, EndorseTarget.APPLICANT_AREA_ID),
          ADDRESS: this.handleGetOriginalVal(opt.TARGET, EndorseTarget.APPLICANT_ADDRESS),
          CITY_ID_NAME: commonService.getCityName(this.state.postCodes, this.handleGetOriginalVal(opt.TARGET, EndorseTarget.APPLICANT_CITY_ID)),
          AREA_ID_NAME: commonService.getAreaName(this.state.postCodes, this.handleGetOriginalVal(opt.TARGET, EndorseTarget.APPLICANT_CITY_ID), this.handleGetOriginalVal(opt.TARGET, EndorseTarget.APPLICANT_AREA_ID))
        };
      case EndorseType.CONTACT_EMAIL:
        return {
          EMAIL: this.handleGetOriginalVal(opt.TARGET, EndorseTarget.APPLICANT_EMAIL)
        };
      case EndorseType.CONTACT_MOBILE:
        return {
          MOBILE: this.handleGetOriginalVal(opt.TARGET, EndorseTarget.APPLICANT_MOBILE)
        };
      case EndorseType.MARRIAGE_STATUS:
        return {
          MARRIAGE: this.handleGetOriginalVal(opt.TARGET, EndorseTarget.APPLICANT_MARRIAGE_CODE)
        };
      case EndorseType.RE_SEND_COMPULSORY:
        return {
          POST_CODE: this.handleGetOriginalVal(opt.TARGET, EndorseTarget.POSTCODE),
          CITY_ID: this.handleGetOriginalVal(opt.TARGET, EndorseTarget.CITY_ID),
          AREA_ID: this.handleGetOriginalVal(opt.TARGET, EndorseTarget.AREA_ID),
          ADDRESS: this.handleGetOriginalVal(opt.TARGET, EndorseTarget.ADDRESS),
          CITY_ID_NAME: commonService.getCityName(this.state.postCodes, this.handleGetOriginalVal(opt.TARGET, EndorseTarget.CITY_ID)),
          AREA_ID_NAME: commonService.getAreaName(this.state.postCodes, this.handleGetOriginalVal(opt.TARGET, EndorseTarget.CITY_ID), this.handleGetOriginalVal(opt.TARGET, EndorseTarget.AREA_ID))
        };
      case EndorseType.NAME:
        return {
          NAME: this.handleGetOriginalVal(opt.TARGET, EndorseTarget.APPLICANT_NAME)
        };
      case EndorseType.ENGINE_ID:
        return {
          ENGINE_ID: this.handleGetOriginalVal(opt.TARGET, EndorseTarget.ENGINE_ID)
        };
        case EndorseType.VEHICLE_ID:
        return {
          VEHICLE_ID: this.handleGetOriginalVal(opt.TARGET, EndorseTarget.VEHICLE_ID)
        };
      case EndorseType.CAR_LICENSE: {
        const carLicense = this.handleGetOriginalVal(opt.TARGET, EndorseTarget.CAR_LICENSE);

        return {
          CAR_LICENSE_FRONT: carLicense.replace(/([A-Za-z\d]{0,4})-([A-Za-z\d]{0,4})/, '$1'),
          CAR_LICENSE_BACK: carLicense.replace(/([A-Za-z\d]{0,4})-([A-Za-z\d]{0,4})/, '$2')
        };
      }
      case EndorseType.ISSUE_DATE: {
        const issueDate = this.handleGetOriginalVal(opt.TARGET, EndorseTarget.ISSUE_DATE);
        return {
          ISSUE_DATE_YEAR: issueDate.replace(/([\d]{0,3})([\d]{2})([\d]{2})/, '$1'),
          ISSUE_DATE_MONTH: issueDate.replace(/([\d]{0,3})([\d]{2})([\d]{2})/, '$2'),
          ISSUE_DATE_DATE: issueDate.replace(/([\d]{0,3})([\d]{2})([\d]{2})/, '$3')
        };
      }
      default:
        return {};
    }
  }

  render() {
    /**  
     * @description 表單 Submit 事件
    */
    const handleSubmit = (val: IFormValue) => {
      // console.log('val', val)
      storageService.setItem(storageKeys.CorrectionFormVal, JSON.stringify(val));

      const path = {
        pathname: RouterSettings.MemberCenter.CorrectionPreview,
        state: { correctionFormVal: val }
      }

      this.props.history.push(path);
    }

    /** 
   * @description 取得保單號碼選單
   */
    const handleGetPolicyNoOpts = () => {
      if (!this.state.policyInfos || this.state.policyInfos.length === 0) return;

      return _.map(this.state.policyInfos, (policyInfo: ClaimPolicyNoContent, index: number) => {
        if (!policyInfo.POLICY_NO) return;

        return <option value={policyInfo.POLICY_NO} key={index}>{policyInfo.POLICY_NO}</option>
      })
    }

    /**
     * @description 取得強制證號選單
     */
    const handleGetCompulsoryNoOpts = () => {
      if (!this.state.policyInfos || this.state.policyInfos.length === 0) return;

      return _.map(this.state.policyInfos, (policyInfo: ClaimPolicyNoContent, index: number) => {
        if (!policyInfo.COMPULSORY_NO) return;

        return <option value={policyInfo.COMPULSORY_NO} key={index}>{policyInfo.COMPULSORY_NO}</option>
      })
    }

    /**
   * @description 保單號碼變更事件
   */
    const handlePolicyNoChange = async (val: string, setFieldValue: any, isCompulsory?: boolean) => {
      if (!val) return;
      // 取得保單資訊
      let policyInfo;
      if (isCompulsory) {
        policyInfo = _.find(this.state.policyInfos, (policyInfo: ClaimPolicyNoContent) => policyInfo.COMPULSORY_NO === val);
      } else {
        policyInfo = _.find(this.state.policyInfos, (policyInfo: ClaimPolicyNoContent) => policyInfo.POLICY_NO === val);
      }

      if (!policyInfo) return;

      // 取得可變更項目
      const endorse = await service.fetchEndorse(this.state.insType, policyInfo.APPLY_NO);
      if (!endorse) return;

      const endorseOpts = this.handleGetEndorseOpts(endorse.OPTION);

      setFieldValue("APPLY_NO", policyInfo.APPLY_NO);
      setFieldValue("COMPULSORY_NO", policyInfo.COMPULSORY_NO);
      setFieldValue("CAR_LICENSE", policyInfo.CAR_LICENSE);
      setFieldValue("ORIGINAL_ENDORSE", endorse);
      setFieldValue("ENDORSE_OPTS", endorseOpts);
    }
/**
 * @description 更新保單號資訊
 */
const handleUpdatePolicyInfos = async (
  currentType: string, currentTypeVal: boolean, cvTypes: CI_CV_TYPE[], setFieldValue: any
) => {
  let compulsoryOnly = false;

  const fetchPolicyNosreq = {
    INS_TYPE_ID: this.state.insType,
    APPLICANT_ID: storageService.getItem(storageKeys.MemberId)
  };

  const policyInfos = await service.fetchPolicyNo(fetchPolicyNosreq) ?? [];
  if (policyInfos.length === 0) return;

  const otherTarget = cvTypes.find((cvType: CI_CV_TYPE) => cvType.TYPE !== currentType);
  if (!otherTarget) return;

  // 當前勾選項目為強制險
  if (currentType === VehicleItemType.CV_TYPE_CI && currentTypeVal) {
    compulsoryOnly = otherTarget.TYPE === VehicleItemType.CV_TYPE_VI && !otherTarget.CHECKED;
  }

  // 當前勾選項目為任意險
  if (currentType === VehicleItemType.CV_TYPE_VI && !currentTypeVal) {
    compulsoryOnly = otherTarget.TYPE === VehicleItemType.CV_TYPE_CI && otherTarget.CHECKED;
  }

  const filteredPolicyInfos = policyInfos.filter((policyInfo: ClaimPolicyNoContent) => {


    if(currentType === VehicleItemType.CV_TYPE_CI && policyInfo.POLICY_NO == ''){

      return policyInfo;
    }else if(currentType === VehicleItemType.CV_TYPE_VI && policyInfo.POLICY_NO == '') {

      return policyInfo;      
    }else if(currentType === VehicleItemType.CV_TYPE_VI) {
      return policyInfo.VOLUNTARY === currentTypeVal && policyInfo.COMPULSORY === otherTarget.CHECKED;
    }else if(currentType === VehicleItemType.CV_TYPE_CI) {
      return policyInfo.VOLUNTARY === currentTypeVal && policyInfo.COMPULSORY !== otherTarget.CHECKED;
    }

  });
  
  this.setState(prevState => ({
    ...prevState,
    policyInfos: filteredPolicyInfos
  }));

  setFieldValue("POLICY_NUMBER", "");
  setFieldValue("COMPULSORY_NO", "");
  setFieldValue("CAR_LICENSE", "");
  setFieldValue("COMPULSORY_ONLY", compulsoryOnly);
};

    return (
      <div className="correction">
        <div className="template-container">
          <div className="template-content">
            <div className="correction__title">批改申請</div>
            <div className="correction__content">
              <Formik
                innerRef={this.formikRef as any}
                initialValues={formInitialValues}
                validationSchema={formValidationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ values, errors, touched, handleBlur, handleChange, setFieldValue, setFieldTouched, setFieldError }) => (
                  <Form>
                    <div className="correction__form">
                      <div className="element__form-table">
                        <div className="element__form-table-tr">
                          <div className="element__form-table-td">
                            {/* 險種 */}
                            <div className="element__form-group">
                              <div className="element__form-group-title" data-fieldname="INS_TYPE">險種</div>
                              <div className="element__form-group-content">
                                {
                                  this.state.insCats && this.state.insCats.length > 0 && _.map(this.state.insCats, (inscat: Inscat, index: number) => {
                                    if (inscat.INS_TYPE_ID === InsType.MobileDevice) return;
                                    return (
                                      <div className="element__radio-group" key={index}>
                                        <label>
                                          <div className="element__radio">
                                            <Field
                                              type="radio"
                                              disabled
                                              name="INS_TYPE"
                                              value={inscat.INS_TYPE_ID}
                                            />
                                            <div className="element__radio-btn" />
                                          </div>
                                          <span>{inscat.INS_TYPE_NAME}</span>
                                        </label>
                                      </div>
                                    );
                                  })
                                }
                              </div>
                              <div className="element__error-message">
                                <ErrorMessage name="INS_TYPE" />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="element__form-table-tr">
                          <div className="element__form-table-td">
                            <div className="element__form-group correction__form__info-container">
                              <div className="element__form-group-content correction__form__info-content">
                                <div className="correction__form__info-content-group">
                                  <div className="correction__form__info-content-group-checkboxs">
                                    {
                                      values.CI_CV_TYPES && values.CI_CV_TYPES.length > 0 && _.map(values.CI_CV_TYPES, (typeInfo: CI_CV_TYPE, index: number) => {
                                        return (
                                          <div className="element__checkbox-group" key={index}>
                                            <label>
                                              <div className="element__checkbox">
                                                <Field
                                                  type="checkbox"
                                                  name={`CI_CV_TYPES[${index}].CHECKED`}
                                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                    handleUpdatePolicyInfos(typeInfo.TYPE, e.currentTarget.checked, values.CI_CV_TYPES, setFieldValue);
                                                    handleChange(`CI_CV_TYPES[${index}].CHECKED`)(e);
                                                  }}
                                                />
                                                <div className="element__checkbox-btn" />
                                              </div>
                                              <span>{typeInfo.TEXT}</span>
                                            </label>
                                          </div>
                                        )
                                      })
                                    }
                                  </div>
                                </div>
                                <div className="correction__form__info-content-group">
                                  <p>{`保單號碼${values.COMPULSORY_ONLY ? '' : '*'}`}</p>
                                  {
                                    values.COMPULSORY_ONLY
                                      ? (
                                        <p>{values.POLICY_NUMBER}</p>
                                      )
                                      : (
                                        <Field
                                          as="select"
                                          name="POLICY_NUMBER"
                                          id="policy-number-input"
                                          className="element__select"
                                          data-fieldname="POLICY_NUMBER"
                                          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                            handlePolicyNoChange(e.currentTarget.value, setFieldValue);
                                            handleChange(e);
                                          }}
                                        >
                                          <option value="" >請選擇</option>
                                          {
                                            handleGetPolicyNoOpts()
                                          }
                                        </Field>
                                      )
                                  }
                                  <div className="element__error-message">
                                    <ErrorMessage name="POLICY_NUMBER" />
                                  </div>
                                </div>
                                <div className="correction__form__info-content-group">
                                  <p>{`強制證號${values.COMPULSORY_ONLY ? '*' : ''}`}</p>
                                  {
                                    values.COMPULSORY_ONLY
                                      ? (
                                        <Field
                                          as="select"
                                          name="COMPULSORY_NO"
                                          id="policy-number-input"
                                          className="element__select"
                                          data-fieldname="POLICY_NUMBER"
                                          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                            handlePolicyNoChange(e.currentTarget.value, setFieldValue, true);
                                            handleChange(e);
                                          }}
                                        >
                                          <option value="" >請選擇</option>
                                          {
                                            handleGetCompulsoryNoOpts()
                                          }
                                        </Field>
                                      )
                                      : (
                                        <p>{values.COMPULSORY_NO}</p>
                                      )
                                  }
                                  <div className="element__error-message">
                                    <ErrorMessage name="COMPULSORY_NO" />
                                  </div>
                                </div>
                                <div className="correction__form__info-content-group">
                                  <p>車號</p>
                                  <p>{values.CAR_LICENSE}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="element__form-table-tr">
                          <div className="element__form-table-td">
                            {/* 變更項目 */}
                            <div className="element__form-group">
                              <div className="element__form-group-title" data-fieldname="ENDORSE_OPTS" >變更項目</div>
                              <div className="element__form-group-content">
                                <div className="correction__list">
                                  <FieldArray name="ENDORSE_OPTS">
                                    {
                                      () => (
                                        <>
                                          {
                                            values.ENDORSE_OPTS && values.ENDORSE_OPTS.length > 0 && (
                                              _.map(values.ENDORSE_OPTS, (opt: EndorseOption, index: number) => {
                                                // console.log('opt', opt)
                                                return (
                                                  <CorrectionEndorseItem
                                                    insTypeId={this.state.insType}
                                                    endorseOpt={opt}
                                                    endorseOptIndex={index}
                                                    key={index}
                                                    values={values}
                                                    errors={errors}
                                                    touched={touched}
                                                    setFieldValue={setFieldValue}
                                                    setFieldTouched={setFieldTouched}
                                                    setFieldError={setFieldError}
                                                    handleChange={handleChange}
                                                    handleBlur={handleBlur}
                                                    postCodes={this.state.postCodes}
                                                    emailDomains={this.state.emailDomains}
                                                    rdStDataContent={[]}
                                                    mortgageBanks={[]}
                                                    yesnoSysOpts={[]}
                                                    marriageSysOpts={this.state.marriageSysOpts}
                                                    UICodes={this.state.UICodes}
                                                  />
                                                );
                                              })
                                            )
                                          }
                                        </>
                                      )
                                    }
                                  </FieldArray>
                                  {
                                    // <Fold isOpen={values.CORRECTION_VEHICAL_BODY_NUMBER_CHECKBOX || values.CORRECTION_VEHICAL_NUMBER_CHECKBOX || values.CORRECTION_OFFER_LICENSE_CHECKBOX}>
                                    //   <p className="correction__remind">※ 提醒：7 ~ 9 選項申請批改皆需要上傳行照。</p>
                                    //   <div className="correction__upload-field">
                                    //     <div className="correction__item-row">
                                    //       <input type="text" className="element__input" readOnly placeholder="上傳證明文件 (行照)" />
                                    //       <label>
                                    //         <div className="claim-service__upload-btn">上傳</div>
                                    //         <Field type="file" name="VEHICLE_LICENSE" accept=".jpg,.jpeg,.png,.gif,.bmp,.tiff" />
                                    //       </label>
                                    //     </div>
                                    //     <div className="element__error-message" />
                                    //   </div>
                                    // </Fold>
                                  }
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="correction__remark">
                      <div className="correction__remark-title">備註</div>
                      <ol>
                        {
                          this.state.endorseCodes && this.state.endorseCodes.length > 0 && (
                            _.map(this.state.endorseCodes, (code: Code, index: number) => <li key={index}>{code.CODE_VALUE}</li>)
                          )
                        }
                      </ol>
                    </div>
                    <div className="correction__controls">
                      <button type="submit" className="element__button element__button--red">預覽畫面</button>
                      <button
                        type="button"
                        className="element__button element__button--red-border"
                        onClick={() => {
                          this.props.history.replace(RouterSettings.MemberCenter.PolicyInquiry);
                        }}
                      >
                        取消
                    </button>
                    </div>
                    <FormikFocusError />
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default CorrectionVehicle;
